import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PagoExitoso() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await fetch(`${API}/checkout/status/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setPaymentData(data);

        if (data.payment_status === "paid") {
          setStatus("success");
        } else if (data.status === "expired") {
          setStatus("expired");
        } else {
          setAttempts((prev) => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        }
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("Error checking status:", error);
      setAttempts((prev) => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 flex items-center justify-center" data-testid="pago-exitoso-page">
      <div className="max-w-md w-full mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          {status === "loading" && (
            <>
              <Loader2 className="w-16 h-16 text-circulo-green mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-serif mb-4">Verificando pago...</h1>
              <p className="text-muted-foreground">
                Estamos confirmando tu transacción. Por favor espera un momento.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="w-20 h-20 text-circulo-green mx-auto mb-6" />
              <h1 className="text-4xl font-serif mb-4">¡Pago exitoso!</h1>
              <p className="text-muted-foreground mb-8">
                Gracias por tu compra. Hemos recibido tu pago correctamente y 
                nos pondremos en contacto contigo pronto.
              </p>
              {paymentData && (
                <div className="p-6 bg-circulo-paper border border-white/10 text-left mb-8">
                  <h3 className="text-sm uppercase tracking-wider text-muted-foreground mb-4">
                    Detalles del pago
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Total:</span>
                      <span className="text-sm font-medium">
                        ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Estado:</span>
                      <span className="text-sm font-medium text-circulo-green">
                        Pagado
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-4">
                <Link to="/" className="btn-primary">
                  Volver al inicio
                </Link>
                <Link to="/contacto" className="btn-secondary">
                  Contactar para agendar
                </Link>
              </div>
            </>
          )}

          {(status === "error" || status === "timeout" || status === "expired") && (
            <>
              <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-6" />
              <h1 className="text-3xl font-serif mb-4">
                {status === "expired" 
                  ? "Sesión expirada" 
                  : "Error en la verificación"}
              </h1>
              <p className="text-muted-foreground mb-8">
                {status === "expired"
                  ? "La sesión de pago ha expirado. Por favor intenta nuevamente."
                  : "No pudimos verificar tu pago. Si completaste el pago, por favor contáctanos."}
              </p>
              <div className="flex flex-col gap-4">
                <Link to="/planes" className="btn-primary">
                  Volver a planes
                </Link>
                <Link to="/contacto" className="btn-secondary">
                  Contactar soporte
                </Link>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
